// Reply detail — the email body on the left, classification metadata on the right.
// Below: an editable composer to send a reply via Gmail API. Pre-fills with the
// auto-draft when one exists; otherwise lets you compose fresh (for escalated
// replies, do_not_contact override, etc.). Reject is available as a secondary
// action when a draft exists and you don't want to send anything.
// for a reason. Two-column on wide screens, stacked on narrow.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  useReplyDetail,
  useAIDraft,
  useApproveDraft,
  useRejectDraft,
  useSendReply,
  type ReplyDetailCreator,
  type ReplyDetailEscalation,
  type ReplyDetailThreadEntry,
} from '../lib/queries';
import { IntentBadge } from '../components/IntentBadge';
import { ConfidenceBar } from '../components/ConfidenceBar';
import type { ReplyIntent } from '@stax/shared';

export function ReplyDetail() {
  const { email_id } = useParams<{ email_id: string }>();
  const detail = useReplyDetail(email_id);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const approve = useApproveDraft();
  const reject = useRejectDraft({
    onSuccess: () => {
      setRejectMode(false);
      setRejectReason('');
    },
  });

  if (detail.isLoading) {
    return (
      <div className="page">
        <BackLink />
        <p className="page__deck"><em>Loading reply…</em></p>
      </div>
    );
  }

  if (detail.error || !detail.data) {
    return (
      <div className="page">
        <BackLink />
        <p className="page__deck page__deck--error">
          Couldn’t load this reply: <code>{detail.error?.message ?? 'no data'}</code>
        </p>
      </div>
    );
  }

  const { email, classification, creator, draft, escalations, thread } = detail.data;

  // Filter the thread to anything other than the focal email itself.
  const otherInThread = (thread ?? []).filter((t) => t.id !== email.id);

  return (
    <div className="page reply-detail">
      <BackLink />

      <header className="page__head reply-detail__head">
        <div className="page__rule">
          <span>Section 01 · Inbox</span>
          <span>Reply Detail</span>
          <span>{new Date(email.received_at).toLocaleString('en-US')}</span>
        </div>
        <h1 className="page__title">{email.subject || '(no subject)'}</h1>
        <p className="page__deck">
          From <strong>{email.from_name ?? email.from_email}</strong>
          {email.from_name && <> · <code>{email.from_email}</code></>}
        </p>
        <div className="reply-detail__head-meta">
          {classification && (
            <>
              <IntentBadge intent={classification.intent as ReplyIntent} />
              <ConfidenceBar value={classification.confidence} />
            </>
          )}
        </div>
      </header>

      {otherInThread.length > 0 && (
        <ThreadStrip entries={otherInThread} />
      )}

      <section className="reply-detail__cols">
        <div className="reply-detail__col reply-detail__col--body">
          <h3 className="reply-detail__col-head">Message</h3>
          <pre className="reply-detail__body">{email.body_text ?? '(empty body)'}</pre>
        </div>

        <aside className="reply-detail__col reply-detail__col--meta">
          <CreatorContext creator={creator} fromEmail={email.from_email} fromName={email.from_name} />

          <h3 className="reply-detail__col-head">Classification</h3>
          {classification ? (
            <dl className="reply-detail__meta">
              <Row label="Intent" value={<IntentBadge intent={classification.intent as ReplyIntent} />} />
              <Row label="Confidence" value={<ConfidenceBar value={classification.confidence} />} />
              <Row label="Date" value={classification.extracted_date ?? '—'} />
              <Row label="Meeting time" value={classification.extracted_meeting_time ?? '—'} />
              <Row label="Mentions money" value={boolMark(classification.mentions_money)} />
              <Row label="Mentions contract" value={boolMark(classification.mentions_contract)} />
              <Row label="Mentions Pedro" value={boolMark(classification.mentions_pedro)} />
              <Row label="Model" value={<code>{classification.model_used}</code>} />
              {classification.reasoning && (
                <Row label="Reasoning" value={<em>{classification.reasoning}</em>} />
              )}
            </dl>
          ) : (
            <p className="muted">Not classified yet.</p>
          )}

          <EscalationsBlock escalations={escalations ?? []} />
        </aside>
      </section>

      <section className="reply-detail__draft">
        <h3 className="reply-detail__col-head">
          Send reply
          {draft?.status === 'pending' && (
            <span className="composer__source">auto-drafted</span>
          )}
          {draft?.status === 'sent' && (
            <span className="composer__source composer__source--sent">already sent</span>
          )}
          {draft?.status === 'rejected' && (
            <span className="composer__source composer__source--rejected">prior draft rejected</span>
          )}
        </h3>

        {draft?.status === 'sent' ? (
          <p className="muted">
            Reply was sent {draft.approved_at ? `at ${new Date(draft.approved_at).toLocaleString()}` : ''}.
            To send a follow-up, use Gmail directly for now.
          </p>
        ) : (
          <Composer
            emailId={email_id ?? ''}
            originalSubject={email.subject}
            defaultSubject={draft?.subject}
            defaultBody={draft?.body_text}
            draftId={draft?.status === 'pending' ? draft.id : undefined}
          />
        )}

        {draft?.status === 'pending' && !rejectMode && (
          <button
            type="button"
            className="composer__reject-link"
            onClick={() => setRejectMode(true)}
          >
            Reject this draft instead (don't send)
          </button>
        )}

        {draft?.status === 'pending' && rejectMode && (
          <form
            className="reject-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (!email_id) return;
              reject.mutate({
                draftId: draft.id,
                emailId: email_id,
                reason: rejectReason.trim() || 'no reason given',
              });
            }}
          >
            <label className="reject-form__field">
              <span>Why reject?</span>
              <input
                autoFocus
                type="text"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="e.g. tone is off, will reply manually"
              />
            </label>
            <div className="reject-form__buttons">
              <button
                type="button"
                className="draft-action"
                onClick={() => {
                  setRejectMode(false);
                  setRejectReason('');
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="draft-action draft-action--reject"
                disabled={reject.isPending}
              >
                {reject.isPending ? 'Rejecting…' : 'Confirm reject'}
              </button>
            </div>
          </form>
        )}

        {draft?.status === 'rejected' && draft.rejection_reason && (
          <p className="muted">
            Prior draft rejected: <em>{draft.rejection_reason}</em>. Compose a fresh reply above.
          </p>
        )}

        {(approve.error || reject.error) && (
          <p className="page__deck--error">
            {(approve.error ?? reject.error)?.message}
          </p>
        )}
      </section>
    </div>
  );
}

// ------- helpers -----------------------------------------------------------

function BackLink() {
  return (
    <Link to="/" className="reply-detail__back">
      ← Back to Inbox
    </Link>
  );
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </>
  );
}

function boolMark(b: boolean): ReactNode {
  return b ? <span className="signal">Yes</span> : '—';
}

function ThreadStrip({ entries }: { entries: ReplyDetailThreadEntry[] }) {
  return (
    <section className="reply-detail__thread">
      <h3 className="reply-detail__col-head">Thread context · {entries.length}</h3>
      <ol className="thread-strip">
        {entries.map((t) => (
          <li key={t.id} className="thread-strip__item">
            <div className="thread-strip__head">
              <strong>{t.from_email}</strong>
              <span>{new Date(t.received_at).toLocaleString('en-US')}</span>
            </div>
            <div className="thread-strip__subject">{t.subject || '(no subject)'}</div>
            <pre className="thread-strip__body">{(t.body_text ?? '').slice(0, 600)}</pre>
          </li>
        ))}
      </ol>
    </section>
  );
}

function EscalationsBlock({ escalations }: { escalations: ReplyDetailEscalation[] }) {
  if (!escalations.length) return null;
  return (
    <div className="reply-detail__escalations">
      <h4 className="reply-detail__sub-head">Escalations</h4>
      <ul>
        {escalations.map((e) => (
          <li key={e.id} className={`escalation escalation--${e.severity}`}>
            <span className="escalation__reason">{e.reason}</span>
            <span className="escalation__sev">{e.severity}</span>
            {e.notes && <p className="escalation__notes">{e.notes}</p>}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ------- composer ---------------------------------------------------------

function Composer({
  emailId,
  originalSubject,
  defaultSubject,
  defaultBody,
  draftId,
}: {
  emailId: string;
  originalSubject: string | null;
  defaultSubject?: string | null;
  defaultBody?: string | null;
  draftId?: string;
}) {
  const reSubject = (s: string | null | undefined): string => {
    if (!s) return 'Re:';
    return /^re:\s/i.test(s) ? s : `Re: ${s}`;
  };

  const [subject, setSubject] = useState<string>(defaultSubject ?? reSubject(originalSubject));
  const [bodyText, setBodyText] = useState<string>(defaultBody ?? '');
  const [showSent, setShowSent] = useState(false);
  // Track whether the user has manually edited the body — if they have, AI
  // draft results don't overwrite their typing.
  const userTouchedBody = useRef(false);
  // Avoid auto-firing AI draft more than once per mount.
  const aiAutoFired = useRef(false);

  const send = useSendReply();
  const aiDraft = useAIDraft();

  // Auto-fire AI draft once on mount if we have no body to start with.
  useEffect(() => {
    if (
      !aiAutoFired.current &&
      !defaultBody &&
      !aiDraft.isPending &&
      !aiDraft.data &&
      emailId
    ) {
      aiAutoFired.current = true;
      aiDraft.mutate({ emailId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailId, defaultBody]);

  // When AI draft returns, fill the composer (unless user already typed).
  useEffect(() => {
    if (!aiDraft.data) return;
    if (userTouchedBody.current) return;
    setBodyText(aiDraft.data.body_text);
    if (aiDraft.data.subject) setSubject(aiDraft.data.subject);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiDraft.data]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!subject.trim() || !bodyText.trim()) return;
    send.mutate(
      { emailId, subject: subject.trim(), body_text: bodyText, draft_id: draftId },
      {
        onSuccess: () => {
          setShowSent(true);
        },
      },
    );
  }

  function handleRegenerate() {
    userTouchedBody.current = false;
    aiDraft.mutate({ emailId });
  }

  if (showSent) {
    return (
      <div className="composer composer--sent">
        <p className="composer__sent-msg">
          ✓ Reply sent. It's now in <code>ass.emails_sent</code>, and Gmail has it in its
          Sent folder for <code>bianchi.luc9@gmail.com</code>.
        </p>
      </div>
    );
  }

  const disabled = send.isPending || !subject.trim() || !bodyText.trim();
  const aiActive = aiDraft.isPending;

  return (
    <form className="composer" onSubmit={handleSubmit}>
      <label className="composer__field">
        <span className="composer__label">Subject</span>
        <input
          type="text"
          className="composer__subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          required
        />
      </label>
      <label className="composer__field">
        <span className="composer__label">
          Message
          {aiActive && <span className="composer__ai-status"> · ✨ Haiku is drafting…</span>}
          {aiDraft.data && !aiActive && (
            <span className="composer__ai-status composer__ai-status--ok">
              {' · ✨ AI drafted (' + aiDraft.data.model_used + ')'}
            </span>
          )}
        </span>
        <textarea
          className="composer__body"
          value={bodyText}
          onChange={(e) => {
            userTouchedBody.current = true;
            setBodyText(e.target.value);
          }}
          rows={12}
          placeholder={aiActive ? 'AI is drafting your reply…' : 'Write your reply…'}
          disabled={aiActive && !bodyText}
          required
        />
      </label>
      {aiDraft.isError && (
        <div className="composer__ai-error">
          AI draft unavailable ({aiDraft.error?.message}). Write your own below.
        </div>
      )}
      <div className="composer__actions">
        <button
          type="submit"
          className="composer__send"
          disabled={disabled}
        >
          {send.isPending ? 'Sending via Gmail…' : draftId ? 'Send (uses draft)' : 'Send reply'}
        </button>
        <button
          type="button"
          className="composer__ai-btn"
          onClick={handleRegenerate}
          disabled={aiActive}
        >
          {aiActive ? '…' : '✨ Regenerate with AI'}
        </button>
        <span className="composer__from">
          From <code>bianchi.luc9@gmail.com</code> · threaded to original
        </span>
      </div>
      {send.isError && (
        <div className="composer__error">
          Send failed: <code>{send.error?.message}</code>
        </div>
      )}
    </form>
  );
}

// ----------------------------------------------------------------------------
// CreatorContext — sidebar blurb that tells you who you're replying to.
//
// Pedro asked for this (2026-05-20) because the daily ~100-creator volume
// makes it impossible to remember everyone individually. Shows everything
// we already know about the creator: channel, platform, niche, subscribers,
// link to their page, when we first contacted them, and how many times
// they've replied. A "FIRST REPLY" badge appears when reply_count === 1
// so the high-signal moment is visible at a glance.

function CreatorContext({
  creator,
  fromEmail,
  fromName,
}: {
  creator: ReplyDetailCreator | null;
  fromEmail: string;
  fromName: string | null;
}) {
  if (!creator) {
    // We have the inbound email but no row in ass.creators yet — this happens
    // when discovery sourced them under a different email or the sync hasn't
    // run. Show what we have, flag the gap honestly.
    return (
      <section className="creator-context creator-context--unknown">
        <h3 className="reply-detail__col-head">Creator context</h3>
        <p className="creator-context__sub">
          We don't have this creator in our ledger yet.
        </p>
        <dl className="creator-context__meta">
          <Row label="From" value={fromName ?? fromEmail} />
          {fromName && <Row label="Email" value={<code>{fromEmail}</code>} />}
        </dl>
      </section>
    );
  }

  const isFirstReply = creator.reply_count === 1;
  const platform = (creator.platform ?? '').toLowerCase();
  const platformLabel = creator.platform
    ? creator.platform.charAt(0).toUpperCase() + creator.platform.slice(1)
    : '—';

  return (
    <section className={`creator-context ${isFirstReply ? 'creator-context--first' : ''}`}>
      <header className="creator-context__head">
        <h3 className="reply-detail__col-head" style={{ margin: 0 }}>About this creator</h3>
        {isFirstReply && <span className="creator-context__badge">FIRST REPLY</span>}
      </header>

      <div className="creator-context__title">
        {creator.canonical_name || fromName || fromEmail}
      </div>
      {creator.handle && (
        <div className="creator-context__handle">@{creator.handle.replace(/^@/, '')}</div>
      )}

      <dl className="creator-context__meta">
        <Row label="Platform" value={platformLabel} />
        {creator.primary_niche && <Row label="Niche" value={creator.primary_niche} />}
        {creator.subscriber_count !== null && creator.subscriber_count > 0 && (
          <Row
            label={platform === 'podcast' ? 'Audience' : 'Subscribers'}
            value={creator.subscriber_count.toLocaleString()}
          />
        )}
        {creator.tier && <Row label="Tier" value={creator.tier} />}
        {creator.first_contacted_at && (
          <Row
            label="First contacted"
            value={new Date(creator.first_contacted_at).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          />
        )}
        {creator.last_contacted_at && creator.last_contacted_at !== creator.first_contacted_at && (
          <Row
            label="Last contacted"
            value={new Date(creator.last_contacted_at).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          />
        )}
        {creator.reply_count > 1 && (
          <Row label="Replies received" value={`${creator.reply_count} (total)`} />
        )}
        {creator.video_post_referenced && (
          <Row
            label="Originally referenced"
            value={<em>"{creator.video_post_referenced}"</em>}
          />
        )}
      </dl>

      {creator.profile_url && (
        <a
          href={creator.profile_url}
          target="_blank"
          rel="noopener noreferrer"
          className="creator-context__link"
        >
          View on {platformLabel} →
        </a>
      )}

      {creator.notes && (
        <p className="creator-context__notes" title="From ass.creators.notes">
          {creator.notes}
        </p>
      )}
    </section>
  );
}
